import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import { TokenConfigType } from '@/constants/types/currency';
import { pinnedTokens } from '@/constants/project-specific/tokens';
import { RequiredTokenListType } from '@/constants/project-specific';
import { fallbackChainId } from '@/constants/chain-specific';
import { AnimatePresence, motion } from 'motion/react';
import { formatPrice } from '@/util/portfolio/formatPrice';
import { debounce } from 'lodash';
import { TokenStats } from '@/lib/price-data/getTokenStats';
interface FloatingTokenProps {
  chainId: RequiredTokenListType;
  tokenPrices?: Record<string, number>;
  tokenStats?: Record<string, TokenStats>;
  onTokenSelect?: (token: TokenConfigType) => void;
}
interface TokenWithPosition extends TokenConfigType {
  x: number;
  y: number;
  scale: number;
}
const FloatingTokens = t0 => {
  const $ = _c(30);
  const {
    chainId,
    tokenPrices,
    tokenStats,
    onTokenSelect
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [floatingTokens, setFloatingTokens] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      width: typeof window !== "undefined" ? window.innerWidth : 0,
      height: typeof window !== "undefined" ? window.innerHeight : 0
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [windowSize, setWindowSize] = useState(t2);
  let t3;
  if ($[2] !== windowSize.height || $[3] !== windowSize.width) {
    t3 = () => {
      const area = windowSize.width * windowSize.height;
      if (area < 300000) {
        return 5;
      }
      if (area < 600000) {
        return 6;
      }
      return 8;
    };
    $[2] = windowSize.height;
    $[3] = windowSize.width;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const getTokenCount = t3;
  let t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const handleResize = debounce(() => {
        setWindowSize({
          width: window.innerWidth,
          height: window.innerHeight
        });
      }, 250);
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    };
    t5 = [];
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  if ($[7] !== chainId || $[8] !== getTokenCount || $[9] !== windowSize.height || $[10] !== windowSize.width) {
    t6 = () => {
      const availableTokens = pinnedTokens[chainId ?? fallbackChainId].display;
      const numTokens = getTokenCount();
      const selectedTokens = [];
      const isInCenter = (x, y) => {
        const centerX = windowSize.width / 2;
        const centerY = windowSize.height / 2;
        const centerWidth = windowSize.width * 0.5;
        const centerHeight = windowSize.height * 0.6;
        return Math.abs(x - centerX) < centerWidth / 2 && Math.abs(y - centerY) < centerHeight / 2;
      };
      const isTooClose = (x_0, y_0, existingTokens) => {
        const minDistance = Math.min(windowSize.width, windowSize.height) * 0.15;
        return existingTokens.some(token => {
          const distance = Math.sqrt(Math.pow(token.x - x_0, 2) + Math.pow(token.y - y_0, 2));
          return distance < minDistance;
        });
      };
      const getRandomPositionInQuadrant = quadrant => {
        const overflow = Math.min(windowSize.width, windowSize.height) * 0.1;
        const screenPadding = Math.min(windowSize.width, windowSize.height) * 0.05;
        const quadrantWidth = (windowSize.width - screenPadding * 2) / 2;
        const quadrantHeight = (windowSize.height - screenPadding * 2 - 60) / 2;
        let x_1;
        let y_1;
        const randomOffset = _temp;
        bb40: switch (quadrant) {
          case 0:
            {
              x_1 = quadrantWidth * randomOffset() - overflow;
              y_1 = 60 + quadrantHeight * randomOffset() - overflow;
              break bb40;
            }
          case 1:
            {
              x_1 = windowSize.width - quadrantWidth * randomOffset() + overflow;
              y_1 = 60 + quadrantHeight * randomOffset() - overflow;
              break bb40;
            }
          case 2:
            {
              x_1 = quadrantWidth * randomOffset() - overflow;
              y_1 = windowSize.height - quadrantHeight * randomOffset() + overflow;
              break bb40;
            }
          default:
            {
              x_1 = windowSize.width - quadrantWidth * randomOffset() + overflow;
              y_1 = windowSize.height - quadrantHeight * randomOffset() + overflow;
            }
        }
        x_1 = Math.max(-24, Math.min(x_1, windowSize.width - 19.2));
        y_1 = Math.max(60, Math.min(y_1, windowSize.height - 19.2));
        return {
          x: x_1,
          y: y_1
        };
      };
      const getRandomPosition = existingTokens_0 => {
        let position;
        let attempts = 0;
        do {
          const baseQuadrant = selectedTokens.length % 4;
          const quadrant_0 = Math.random() < 0.3 ? Math.floor(Math.random() * 4) : baseQuadrant;
          position = getRandomPositionInQuadrant(quadrant_0);
          attempts++;
        } while ((isTooClose(position.x, position.y, existingTokens_0) || isInCenter(position.x, position.y)) && attempts < 100);
        return position;
      };
      let placementAttempts = 0;
      while (selectedTokens.length < numTokens && availableTokens.length > 0 && placementAttempts < 3) {
        const randomIndex = Math.floor(Math.random() * availableTokens.length);
        const token_0 = availableTokens[randomIndex];
        const position_0 = getRandomPosition(selectedTokens);
        if (position_0) {
          selectedTokens.push({
            ...token_0,
            ...position_0,
            scale: 1 + Math.random() * 0.4
          });
          availableTokens.splice(randomIndex, 1);
        } else {
          placementAttempts++;
        }
      }
      setFloatingTokens(selectedTokens);
    };
    $[7] = chainId;
    $[8] = getTokenCount;
    $[9] = windowSize.height;
    $[10] = windowSize.width;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== chainId || $[13] !== windowSize) {
    t7 = [chainId, windowSize];
    $[12] = chainId;
    $[13] = windowSize;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  useEffect(t6, t7);
  let t8;
  if ($[15] !== floatingTokens || $[16] !== onTokenSelect || $[17] !== tokenPrices || $[18] !== tokenStats || $[19] !== windowSize.height || $[20] !== windowSize.width) {
    let t9;
    if ($[22] !== onTokenSelect || $[23] !== tokenPrices || $[24] !== tokenStats || $[25] !== windowSize.height || $[26] !== windowSize.width) {
      t9 = token_1 => {
        const isNearBottom = token_1.y > windowSize.height * 0.75;
        const isNearRight = token_1.x > windowSize.width * 0.75;
        const isNearLeft = token_1.x < windowSize.width * 0.25;
        const tooltipPositionClasses = ["absolute", isNearRight ? "right-0 translate-x-0" : isNearLeft ? "left-0 translate-x-0" : "left-1/2 -translate-x-1/2", isNearBottom ? "bottom-[calc(100%+1rem)]" : "-bottom-20"].join(" ");
        return <motion.div key={token_1.address} initial={{
          x: token_1.x,
          y: token_1.y,
          scale: token_1.scale,
          filter: "blur(4px)",
          opacity: 0.1
        }} animate={{
          x: token_1.x,
          y: [token_1.y - 20, token_1.y + 20],
          opacity: 0.2
        }} whileHover={{
          scale: token_1.scale * 1.1,
          filter: "blur(0px)",
          opacity: 1,
          transition: {
            scale: {
              duration: 0.2,
              ease: "easeOut"
            },
            filter: {
              duration: 0.2,
              ease: "easeOut"
            }
          }
        }} transition={{
          y: {
            duration: 6 + Math.random() * 2.2,
            repeat: Infinity,
            repeatType: "reverse",
            ease: "easeInOut"
          }
        }} onHoverStart={_temp2} onHoverEnd={_temp3} className="absolute group cursor-pointer pointer-events-auto" onClick={() => onTokenSelect?.(token_1)}><div className="relative"><motion.img src={token_1.logo} alt={token_1.name} className="w-12 h-12 rounded-full" style={{
              filter: "blur(4px)"
            }} whileHover={{
              filter: "blur(0px)",
              opacity: 1,
              transition: {
                duration: 0.2,
                ease: "easeOut"
              }
            }} /><div className={`${tooltipPositionClasses} group-hover:opacity-100 transition-opacity duration-200 bg-black/80 rounded-lg p-3 whitespace-nowrap`}><div className="text-sm font-medium">{token_1.symbol}{tokenStats?.[token_1.address?.toLowerCase()]?.priceChange?.h24 ? <span className={`text-sm ${tokenStats[token_1.address?.toLowerCase()].priceChange.h24 >= 0 ? "text-green-500" : "text-red-500"}`}>{" "}{tokenStats[token_1.address?.toLowerCase()].priceChange.h24 >= 0 ? "+" : ""}{tokenStats[token_1.address?.toLowerCase()].priceChange.h24.toFixed(2)}%</span> : null}</div>{tokenPrices?.[token_1.address?.toLowerCase()] ? <div className="flex items-center gap-2"><span className="text-sm text-gray-400">${formatPrice(Number(tokenPrices[token_1.address?.toLowerCase()]) ?? 0)}</span></div> : <span className="text-sm text-gray-400">Loading...</span>}</div></div></motion.div>;
      };
      $[22] = onTokenSelect;
      $[23] = tokenPrices;
      $[24] = tokenStats;
      $[25] = windowSize.height;
      $[26] = windowSize.width;
      $[27] = t9;
    } else {
      t9 = $[27];
    }
    t8 = floatingTokens.map(t9);
    $[15] = floatingTokens;
    $[16] = onTokenSelect;
    $[17] = tokenPrices;
    $[18] = tokenStats;
    $[19] = windowSize.height;
    $[20] = windowSize.width;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[28] !== t8) {
    t9 = <div className="fixed inset-0 pointer-events-none overflow-hidden"><AnimatePresence>{t8}</AnimatePresence></div>;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  return t9;
};
export default FloatingTokens;
function _temp() {
  return Math.random() * 0.6 + 0.2;
}
function _temp2(e) {
  const element = e.target as HTMLElement;
  element.style.animationPlayState = "paused";
}
function _temp3(e_0) {
  const element_0 = e_0.target as HTMLElement;
  element_0.style.animationPlayState = "running";
}